/**
 * Revenue Dashboard (Phase 9H)
 * Executive dashboard with pipeline, revenue, and forecast metrics
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSign, TrendingUp, Target, Users, Flame, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  hot: '#ef4444',
  warm: '#f97316',
  cold: '#3b82f6',
  gold: '#d4af37'
};

const RevenueDashboard = ({ user }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(`${API}/revenue/dashboard`, {
          headers: authHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setMetrics(data);
        }
      } catch (error) {
        console.error('Error fetching revenue metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [authHeaders]);

  const ExecutiveMetric = ({ icon: Icon, label, value, subtext, color = 'text-white' }) => (
    <Card className="glass-panel border-white/10">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${color}`} />
          <p className="text-xs text-white/60 uppercase tracking-wide">{label}</p>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-32 bg-white/10" />
        ) : (
          <>
            <strong className={`text-3xl font-semibold ${color}`}>{value}</strong>
            {subtext && <p className="text-xs text-white/60 mt-1">{subtext}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="page-stack">
        <section className="hero-panel ceo-hero glass-panel">
          <div>
            <p className="eyebrow">Phase 9H — Executive View</p>
            <h2>Revenue Dashboard</h2>
          </div>
        </section>
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="glass-panel border-white/10">
              <CardContent className="pt-6">
                <Skeleton className="h-10 w-full bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </section>
      </div>
    );
  }

  // Prepare chart data
  const pipelineStageData = Object.entries(metrics?.pipeline_by_stage || {}).map(([stage, count]) => ({
    name: stage,
    value: count
  }));

  const leadDistributionData = [
    { name: 'Hot Leads', value: metrics?.hot_leads || 0, color: COLORS.hot },
    { name: 'Warm Leads', value: metrics?.warm_leads || 0, color: COLORS.warm },
    { name: 'Cold Leads', value: metrics?.cold_leads || 0, color: COLORS.cold }
  ];

  const sourceData = Object.entries(metrics?.lead_sources || {}).map(([source, count]) => ({
    name: source,
    count
  }));

  return (
    <div className="page-stack" data-testid="revenue-dashboard">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9H — Executive View</p>
          <h2>Revenue Dashboard</h2>
          <div className="hero-stats">
            <span>{metrics?.total_leads || 0} Total Leads</span>
            <span>${Math.round((metrics?.estimated_pipeline || 0) / 1000)}K Pipeline</span>
            <span>{metrics?.conversion_rate || 0}% Conversion</span>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ExecutiveMetric
          icon={DollarSign}
          label="Estimated Pipeline"
          value={`$${Math.round((metrics?.estimated_pipeline || 0) / 1000)}K`}
          color="text-green-400"
        />
        <ExecutiveMetric
          icon={TrendingUp}
          label="Recoverable Revenue"
          value={`$${Math.round((metrics?.recoverable_revenue || 0) / 1000)}K`}
          subtext="Hot + Warm leads"
          color="text-[var(--gold)]"
        />
        <ExecutiveMetric
          icon={Target}
          label="Conversion Rate"
          value={`${metrics?.conversion_rate || 0}%`}
          subtext="Qualified to Closed Won"
          color="text-blue-400"
        />
        <ExecutiveMetric
          icon={Users}
          label="Total Leads"
          value={metrics?.total_leads || 0}
          color="text-white"
        />
      </section>

      {/* Lead Distribution & Pipeline */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Lead Distribution */}
        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90 flex items-center gap-2">
              <Flame className="h-5 w-5 text-[var(--gold)]" />
              Lead Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={leadDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {leadDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-400">{metrics?.hot_leads || 0}</p>
                <p className="text-xs text-white/60">Hot</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-400">{metrics?.warm_leads || 0}</p>
                <p className="text-xs text-white/60">Warm</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-400">{metrics?.cold_leads || 0}</p>
                <p className="text-xs text-white/60">Cold</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pipeline by Stage */}
        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[var(--gold)]" />
              Pipeline by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={pipelineStageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" tick={{ fill: '#999', fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                <YAxis tick={{ fill: '#999' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                <Bar dataKey="value" fill={COLORS.gold} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </section>

      {/* Lead Sources */}
      <section>
        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90">Lead Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {sourceData.map((source, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-2xl font-bold text-[var(--gold)]">{source.count}</p>
                  <p className="text-xs text-white/60 mt-1">{source.name}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default RevenueDashboard;
